import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router'
import { LanguageProvider } from './context/LanguageContext'
import { CartProvider } from './context/CartContext'
import { CatalogProvider, useCatalog } from './context/CatalogContext'
import HomePage from './pages/HomePage'
import Navigation  from './sections/Navigation'
import Footer      from './sections/Footer'
import ScrollToTop from './components/ScrollToTop'
import CookieBanner from './components/CookieBanner'
import GoogleAnalytics from './components/GoogleAnalytics'
import DelayedNewsletterPopup from './components/DelayedNewsletterPopup'
import CartDrawer from './components/CartDrawer'
import StagingAccessGate from './components/StagingAccessGate'

const ContactPage = lazy(() => import('./pages/ContactPage'))
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'))
const CGV = lazy(() => import('./pages/CGV'))
const Livraison = lazy(() => import('./pages/Livraison'))
const PolitiqueConfidentialite = lazy(() => import('./pages/PolitiqueConfidentialite'))
const MediaPage = lazy(() => import('./pages/Media'))
const PartnersPage = lazy(() => import('./pages/PartnersPage'))
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'))
const NewsletterArchivesPage = lazy(() => import('./pages/NewsletterArchivesPage'))
const NewsletterIssuePage = lazy(() => import('./pages/NewsletterIssuePage'))
const NewsletterUnsubscribePage = lazy(() => import('./pages/NewsletterUnsubscribePage'))
const SecretMusic = lazy(() => import('./pages/SecretMusic'))
const ShopPage = lazy(() => import('./pages/ShopPage'))
const ProductPage = lazy(() => import('./pages/ProductPage'))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'))
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'))
const WithdrawalPage = lazy(() => import('./pages/WithdrawalPage'))
const NotFound = lazy(() => import('./pages/NotFound'))

const MIN_LOADER_MS = 320
const MAX_LOADER_MS = 6000

function ContentReadyNotifier({ onReady }) {
  const location = useLocation()

  useEffect(() => {
    onReady()
  }, [location.pathname, onReady])

  return null
}

function ShopOnly() {
  const { shopEnabled, catalogReady } = useCatalog()
  if (!catalogReady) return null
  if (!shopEnabled) return <Navigate to="/" replace />
  return <Outlet />
}

function AppRoutes({ onContentReady }) {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/"                   element={<HomePage />} />
        <Route path="/contact"            element={<ContactPage />} />
        <Route path="/mentions-legales"   element={<MentionsLegales />} />
        <Route path="/cgv"                element={<CGV />} />
        <Route path="/livraison"          element={<Livraison />} />
        <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/about"              element={<Navigate to="/" replace />} />
        <Route path="/projet"             element={<Navigate to="/#project" replace />} />
        <Route path="/media"              element={<MediaPage />} />
        <Route path="/partenaires"        element={<PartnersPage />} />
        <Route path="/newsletter"         element={<NewsletterPage />} />
        <Route path="/newsletter/desinscription" element={<NewsletterUnsubscribePage />} />
        <Route path="/newsletter/archives" element={<NewsletterArchivesPage />} />
        <Route path="/newsletter/:slug"   element={<NewsletterIssuePage />} />
        <Route path="/preview-qileoNG"    element={<SecretMusic />} />
        <Route path="/blog"               element={<Navigate to="/" replace />} />
        <Route path="/blog/:slug"         element={<Navigate to="/" replace />} />
        <Route element={<ShopOnly />}>
          <Route path="/boutique"         element={<ShopPage />} />
          <Route path="/boutique/:slug"   element={<ProductPage />} />
        </Route>
        <Route path="/commande"           element={<CheckoutPage />} />
        <Route path="/commande/confirmation/:token" element={<OrderConfirmationPage />} />
        <Route path="/retractation"       element={<WithdrawalPage />} />
        <Route path="*"                   element={<NotFound />} />
      </Routes>
      <ContentReadyNotifier onReady={onContentReady} />
    </Suspense>
  )
}

function AppShell() {
  const location = useLocation()
  const [isPageLoading, setIsPageLoading] = useState(false)
  const prevPathnameRef = useRef(location.pathname)
  const isInitialMountRef = useRef(true)
  const navigationIdRef = useRef(0)
  const loaderStartedAtRef = useRef(0)
  const hideLoaderTimerRef = useRef(null)

  const clearHideLoaderTimer = useCallback(() => {
    if (hideLoaderTimerRef.current) {
      clearTimeout(hideLoaderTimerRef.current)
      hideLoaderTimerRef.current = null
    }
  }, [])

  const beginLoading = useCallback(() => {
    navigationIdRef.current += 1
    clearHideLoaderTimer()
    loaderStartedAtRef.current = performance.now()
    setIsPageLoading(true)
  }, [clearHideLoaderTimer])

  const handleContentReady = useCallback(() => {
    const navIdAtReady = navigationIdRef.current
    clearHideLoaderTimer()

    const elapsed = performance.now() - loaderStartedAtRef.current
    const remaining = Math.max(0, MIN_LOADER_MS - elapsed)

    hideLoaderTimerRef.current = setTimeout(() => {
      if (navigationIdRef.current !== navIdAtReady) return
      setIsPageLoading(false)
      hideLoaderTimerRef.current = null
    }, remaining)
  }, [clearHideLoaderTimer])

  useLayoutEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevPathnameRef.current = location.pathname

      if (location.pathname !== '/') {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- loader visible avant peinture
        beginLoading()
      }

      return
    }

    if (location.pathname === prevPathnameRef.current) return

    prevPathnameRef.current = location.pathname
    beginLoading()
  }, [location.pathname, beginLoading])

  useEffect(() => {
    if (!isPageLoading) return undefined

    const navIdAtStart = navigationIdRef.current
    const maxTimer = setTimeout(() => {
      if (navigationIdRef.current !== navIdAtStart) return
      setIsPageLoading(false)
    }, MAX_LOADER_MS)

    return () => clearTimeout(maxTimer)
  }, [isPageLoading])

  useEffect(() => () => clearHideLoaderTimer(), [clearHideLoaderTimer])

  return (
    <>
      <ScrollToTop />
      <div className={`route-loader ${isPageLoading ? 'is-active' : ''}`} aria-hidden={!isPageLoading}>
        <span className="route-loader-dot" />
        <span className="route-loader-dot" />
        <span className="route-loader-dot" />
      </div>
      <div className="min-h-screen bg-ng-beige">
        <Navigation key={location.pathname} />
        <main
          className={`transition-opacity duration-150 ${isPageLoading ? 'min-h-[60vh] opacity-0 pointer-events-none' : 'opacity-100'}`}
          aria-busy={isPageLoading}
        >
          <AppRoutes onContentReady={handleContentReady} />
        </main>
        <Footer />
        <CartDrawer />
        <CookieBanner />
        <GoogleAnalytics />
        <DelayedNewsletterPopup />
      </div>
    </>
  )
}

export default function App() {
  return (
    <StagingAccessGate>
      <BrowserRouter>
        <LanguageProvider>
          <CatalogProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </CatalogProvider>
        </LanguageProvider>
      </BrowserRouter>
    </StagingAccessGate>
  )
}